import { withOwnerPoints } from '../utils/projectOwnerState';
import type { Project, ProjectEvent, PointRecord } from '../types';
import * as reducers from './projectReducers';
import type { ProjectReducer, ProjectReducerContext, ProjectReduction, MintIntent } from './projectReducers';

export const PERSONAL_FIELDS = ['folderId', 'workspaceId', 'parentProjectId', 'sourceTaskId', 'roleId', 'occupationalImpact', 'hobbyId', 'subjectId', 'practiceId', 'discipline', 'activity', 'sharedMirror', 'privateSince', 'retainedPointRecords'] as const;
export function sharedProjectData(project: Project): Project {
  const shared = { ...project };
  for (const key of PERSONAL_FIELDS) delete shared[key];
  delete shared.pointRecord;
  shared.tasks = project.tasks.map(task => {
    const copy = { ...task }; delete copy.linkedProjectId; delete copy.pointRecord;
    if (copy.subTasks) copy.subTasks = copy.subTasks.map(sub => { const item = { ...sub }; delete item.pointRecord; return item; });
    return copy;
  });
  return shared;
}
export function ownerMirror(row: Project, local: Project | undefined, revision?: number): Project {
  const next = sharedProjectData(row);
  for (const key of PERSONAL_FIELDS) {
    if (key !== 'sharedMirror' && local && Object.prototype.hasOwnProperty.call(local, key)) Object.assign(next, { [key]: local[key] });
  }
  next.tasks = next.tasks.map(task => {
    const link = local?.tasks.find(item => item.id === task.id)?.linkedProjectId;
    return link === undefined ? task : { ...task, linkedProjectId: link };
  });
  if (revision !== undefined) next.sharedMirror = { revision };
  return withOwnerPoints(next, local);
}
export function canonical(value: unknown, ignoreUpdated = false): string {
  if (Array.isArray(value)) return `[${value.map(item => canonical(item, ignoreUpdated)).join(',')}]`;
  if (value && typeof value === 'object') return `{${Object.entries(value).filter(([key, val]) => val !== undefined && (!ignoreUpdated || key !== 'updatedAt')).sort(([a], [b]) => a.localeCompare(b)).map(([key, val]) => `${JSON.stringify(key)}:${canonical(val, ignoreUpdated)}`).join(',')}}`;
  return JSON.stringify(value) ?? 'undefined';
}
export const sameContent = (a: Project, b: Project) => canonical(sharedProjectData(a), true) === canonical(sharedProjectData(b), true);

/** Freeze toggles and index-based reorders at the user's original snapshot. */
export function intentReducer<A>(before: Project, reducer: ProjectReducer<A>, args: A, ctx: ProjectReducerContext): (fresh: Project, context: ProjectReducerContext) => ProjectReduction {
  if (reducer === reducers.updateProject) {
    const updates = Object.fromEntries(Object.entries(args as Partial<Project>).filter(([key, value]) => canonical(before[key as keyof Project]) !== canonical(value)));
    return (fresh, context) => reducers.updateProject(fresh, updates, context);
  }
  if (reducer === reducers.addTask || reducer === reducers.addPhase || reducer === reducers.addSubTask) return (fresh, context) => {
    const exists = fresh.tasks.some(task => task.id === ctx.newId || task.subTasks?.some(sub => sub.id === ctx.newId)) || fresh.phases.some(phase => phase.id === ctx.newId);
    return exists ? { project: fresh, events: [], mints: [], warnings: [] } : reducer(fresh, args, context);
  };
  const values = args as { taskId?: string; subTaskId?: string; phaseId?: string };
  if (reducer === reducers.toggleTask) return (fresh, context) => reducers.setTaskCompleted(fresh, { taskId: values.taskId!, completed: !before.tasks.find(t => t.id === values.taskId)?.completed }, context);
  if (reducer === reducers.toggleSubTask) return (fresh, context) => reducers.setSubTaskCompleted(fresh, { taskId: values.taskId!, subTaskId: values.subTaskId!, completed: !before.tasks.find(t => t.id === values.taskId)?.subTasks?.find(s => s.id === values.subTaskId)?.completed }, context);
  if (reducer === reducers.togglePhaseCollapse) return (fresh, context) => reducers.updatePhase(fresh, { phaseId: values.phaseId!, updates: { collapsed: !before.phases.find(p => p.id === values.phaseId)?.collapsed } }, context);
  if (reducer === reducers.reorderTasks || reducer === reducers.reorderPhases) {
    const target = reducer(before, args, ctx).project;
    const field = reducer === reducers.reorderTasks ? 'tasks' : 'phases';
    return fresh => ({ project: { ...fresh, [field]: fresh[field].map(item => ({ ...item, order: target[field].find(next => next.id === item.id)?.order ?? item.order })), updatedAt: ctx.now }, events: [], mints: [], warnings: [] });
  }
  return (fresh, context) => reducer(fresh, args, context);
}

type Change = { path: string[]; before: unknown; after: unknown };
const DERIVED = new Set(['updatedAt', 'progress', 'pointRecord', 'completedBy', 'xpPending']);
function changes(before: unknown, after: unknown, path: string[] = []): Change[] {
  if (canonical(before) === canonical(after)) return [];
  if (Array.isArray(before) && Array.isArray(after) && [...before, ...after].every(v => v && typeof v === 'object' && 'id' in v)) {
    return [...new Set([...before, ...after].map(v => String(v.id)))].flatMap(id => changes(before.find(v => v.id === id), after.find(v => v.id === id), [...path, `@${id}`]));
  }
  if (before && after && typeof before === 'object' && typeof after === 'object' && !Array.isArray(before) && !Array.isArray(after)) {
    const a = before as Record<string, unknown>, b = after as Record<string, unknown>;
    return [...new Set([...Object.keys(a), ...Object.keys(b)])].filter(key => !DERIVED.has(key)).flatMap(key => changes(a[key], b[key], [...path, key]));
  }
  return [{ path, before, after }];
}
function atPath(value: unknown, path: string[]): unknown {
  for (const part of path) {
    if (part.startsWith('@')) value = Array.isArray(value) ? value.find(v => v.id === part.slice(1)) : undefined;
    else value = value && typeof value === 'object' ? (value as Record<string, unknown>)[part] : undefined;
  }
  return value;
}
export function findClash(before: Project, desired: Project, fresh: Project): { field: string; theirs: string } | undefined {
  for (const change of changes(sharedProjectData(before), sharedProjectData(desired))) {
    const theirs = atPath(fresh, change.path);
    if (canonical(theirs) !== canonical(change.before) && canonical(theirs) !== canonical(change.after)) return { field: change.path.join('.'), theirs: typeof theirs === 'string' ? theirs : canonical(theirs) };
  }
}

/** Annotate only new completions, never legacy done-without-record items. */
export function deferCompletion(before: Project, next: Project, actorId: string): Project {
  const mark = <T extends { pointRecord?: PointRecord; completedBy?: string; xpPending?: 'mint' }>(item: T, wasDone: boolean, done: boolean): T => {
    const copy = { ...item };
    if (!done) delete copy.xpPending;
    else if (!wasDone) { copy.completedBy = actorId; copy.xpPending = 'mint'; }
    return copy;
  };
  return { ...mark(next, before.status === 'completed', next.status === 'completed'), tasks: next.tasks.map(task => {
    const old = before.tasks.find(t => t.id === task.id);
    return { ...mark(task, old?.status === 'done', task.status === 'done'), subTasks: task.subTasks?.map(sub => mark(sub, !!old?.subTasks?.find(s => s.id === sub.id)?.completed, sub.completed)) };
  }) };
}
export function resolvePending(project: Project, ctx: ProjectReducerContext, name: (id?: string) => string): ProjectReduction {
  const mints: MintIntent[] = [];
  const multiplier = project.complexity === 'high' ? 1.5 : project.complexity === 'medium' ? 1.2 : 1;
  const resolve = <T extends { pointRecord?: PointRecord; completedBy?: string; xpPending?: 'mint' }>(item: T, done: boolean, kind: keyof ProjectReducerContext['points'], title: string, extra: Partial<MintIntent['metadata']>): T => {
    if (!item.xpPending) return item;
    const next = { ...item }; delete next.xpPending;
    if (done && !next.pointRecord) {
      next.pointRecord = { points: Math.round(ctx.points[kind] * multiplier), userLevel: ctx.userLevel, earnedAt: ctx.now };
      mints.push({ activityName: `${title} (completed by ${name(item.completedBy)})`, domainId: project.category || '', xpEarned: next.pointRecord.points, completedAt: ctx.now, metadata: { projectId: project.id, projectName: project.title, ...extra } });
    }
    return next;
  };
  const next = resolve(project, project.status === 'completed', 'project', project.title, { projectCompleted: true });
  return { project: { ...next, tasks: project.tasks.map(task => ({ ...resolve(task, task.status === 'done', 'task', task.title, { taskId: task.id }), subTasks: task.subTasks?.map(sub => resolve(sub, sub.completed, 'subTask', `${task.title} - ${sub.title}`, { taskId: task.id, subTaskId: sub.id, isSubTask: true })) })) }, mints, events: [], warnings: [] };
}
export function copyHistory(...sources: ProjectEvent[][]): ProjectEvent[] {
  return [...new Map(sources.flat().map(event => [event.id, event])).values()].sort((a, b) => b.at.localeCompare(a.at) || b.id.localeCompare(a.id)).slice(0, 200);
}
